<template>
  <div class="col px-0">
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="text-subtitle-1 text-sm-h6 text-md-h5 font-weight-bold text-center">
      Relatório de Operações Planejadas x Realizadas
    </div>

    <v-card>
      <v-card-title>
        <span>Filtros</span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="harvestId"
              label="Safra"
              :items="harvestList"
              item-value="id"
              item-text="name"
              @change="loadCulturesFromHarvest"
              clearable
              @click:clear="harvestId = null; cultureList = []; cultureId = null"
              :loading="harvestListLoading"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="cultureId"
              label="Cultura"
              :items="harvestId ? cultureList : allCultureList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="cultureId = null"
              @change="loadPeriodsFromHarvestAndCulture"
              :loading="cultureListLoading"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="periodIds"
              label="Ciclo(s)"
              :items="harvestId ? periodList : allPeriodList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="periodIds = []"
              :loading="periodListLoading"
              multiple
              chips
              dense
              deletable-chips
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="removeSelectedPeriod(data.item)"
                >
                  {{ data.item.name }} 
                  <small class="font-italic font-weight-light ml-2 mt-1"> ({{data.item.additionalInfo}}) </small>
                  <small v-if="data.item.secondAdditionalInfo" class="font-italic font-weight-light ml-2 mt-1"> ({{data.item.secondAdditionalInfo}}) </small>
                </v-chip>
              </template>
              <template v-slot:item="data">
                <v-list-item-action>
                  <v-checkbox :input-value="data.attrs.inputValue"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="text-subtitle-1">{{data.item.name}}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{data.item.additionalInfo}}
                    <br v-if="data.item.secondAdditionalInfo">
                    {{data.item.secondAdditionalInfo}}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="fieldIds"
              label="Talhão(ões)"
              :items="fieldsList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="fieldIds = []"
              :loading="harvestListLoading"
              multiple
              chips
              dense
              deletable-chips
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="operationTypeIds"
              label="Tipo(s) de Operação(ões)"
              :items="operationTypeList"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="operationTypeIds = []"
              :loading="harvestListLoading"
              multiple
              chips
              dense
              deletable-chips
            />
          </v-col>

          <v-col cols="12" sm="3">
            <v-autocomplete
              v-model="periodFilterModel"
              label="Período"
              :items="periodFilterOptions"
              item-value="key"
              item-text="name"
            />
          </v-col>
          <v-col cols="12" sm="4" v-if="periodFilterModel == 'OTHER'">
            <DateRangeField
              :inputData.sync="periodFilterRangeModel"
              label="Período Customizado"
              :hasTodayDate="true"
            />
          </v-col>

          <v-col cols="12" sm="2">
            <v-btn 
              @click="loadExecutionOperationReport"
              :disabled="false"
              class="mt-2"
            >
              <v-icon left>mdi-magnify</v-icon>
              Filtrar
            </v-btn>
          </v-col>

          <v-col cols="12" sm="2">
            <v-switch v-model="sideBySideView" inset :label="sideBySideView ? 'Ver lado a lado' : 'Ver abaixo'"></v-switch>
          </v-col>

          <v-col cols="12" sm="2" v-if="sideBySideView" >
            <v-switch v-model="showEmployees" inset :label="showEmployees ? 'Exibe funcionários' : 'Não exibe funcionários'"></v-switch>
          </v-col>

          <v-col cols="12" sm="3" v-if="sideBySideView && showEmployees">
            <v-switch  v-model="showPatrimonies" inset :label="showPatrimonies ? 'Exibe patrimônios e implementos' : 'Não exibe patrimônios e implementos'"></v-switch>
          </v-col>

          <v-col cols="12" sm="2" v-if="sideBySideView">
            <v-switch  v-model="showConsumedItems" inset :label="showConsumedItems ? 'Exibe itens consumidos e produtos produzidos' : 'Não exibe itens consumidos e produtos produzidos'"></v-switch>
          </v-col>

           <v-col cols="12" sm="3" v-if="sideBySideView && showConsumedItems">
            <v-switch  v-model="showConsumedItemDoses" inset :label="showConsumedItemDoses ? 'Exibe dose dos itens consumidos e quantidade dos produtos produzidos' : 'Não exibe dose dos itens consumidos e quantidade dos produtos produzidos'"></v-switch>
          </v-col>

        </v-row>
      </v-card-text>
    </v-card>

    <div>
      <BasePrint
        printableElementId="executionOperationReportTable"
        printButtonLabel="Imprimir"
        printTitle="Relatório de Operações Planejadas x Realizadas"
        @start-printing="printing = loading = true"
        @stop-printing="printing = loading = false"
        v-if="planningExecutionOperationsReport"
        class="mt-3"
      />
    </div>

    <div id="executionOperationReportTable">
      <v-simple-table 
        v-if="planningExecutionOperationsReport"
        class="mt-3"
      >
        <template v-slot:default>

          <v-timeline
            align-top
            :dense="!sideBySideView"
          >
            <v-timeline-item
              v-for="(operationGroup, i) in planningExecutionOperationsReport"
              :key="i + '_operation_group'"
              :color="operationGroup.layout == 'planning' ? shadeColor(operationTypeColor(operationGroup.operationType), 50) : operationTypeColor(operationGroup.operationType)"
              :icon="operationTypeIcon(operationGroup.operationType)"
              :left="operationGroup.layout == 'planning'"
              :right="operationGroup.layout != 'planning'"
              fill-dot
            >
              <template v-slot:opposite v-if="operationGroup.layout == 'planning_execution'">
                <v-card
                  :color="shadeColor(operationTypeColor(operationGroup.operationType), 50)"
                >
                  <v-card-title class="title">
                    {{operationGroup.planningOperationDate | formatDate}} - {{operationGroup.operationTypeText}} - {{operationGroup.culture}} (Planejado)
                  </v-card-title>
                  <v-card-text class="white text--primary">

                    <v-simple-table>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left">Talhão(ões)</th>
                            <th class="text-left">Safra</th>
                            <th class="text-left">Área</th>
                            <th v-if="!sideBySideView || (sideBySideView && showEmployees)" class="text-left">Funcionário</th>
                            <th v-if="!sideBySideView || (sideBySideView && showEmployees && showPatrimonies)" class="text-left">Patrimônio</th>
                            <th v-if="!sideBySideView || (sideBySideView && showEmployees && showPatrimonies)" class="text-left">Implemento</th>
                            <th v-if="!sideBySideView || (sideBySideView && showEmployees)" class="text-left">Duração</th>
                            <th v-if="((!sideBySideView || (sideBySideView && showConsumedItems)) && operationGroup.operationType != 'soil_preparation' && operationGroup.operationType != 'crop')" class="text-left" >Itens Consumidos</th>
                            <th v-if="((!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)) && operationGroup.operationType != 'soil_preparation' && operationGroup.operationType != 'crop')" class="text-left" >Dose/ha</th>
                            <th v-if="((!sideBySideView || (sideBySideView && showConsumedItems)) && operationGroup.operationType == 'crop')" class="text-left" >Produtos Produzidos</th>
                            <th v-if="((!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)) && operationGroup.operationType == 'crop')" class="text-left" >Qtd.</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="operation in operationGroup.planningOperations"
                            :key="operation.operationId + 'operationLine'"
                          >
                            <td v-if="operation.periods">
                              <div  v-for="(period, i) in operation.periods" :key="operation.operationId + period + i">
                                {{ period }}
                              </div>
                            </td>
                            <td>{{ operation.harvest }}</td>
                            <td>{{ operation.totalArea | formatNumber }} ha</td>
                            <td v-if="!sideBySideView || (sideBySideView && showEmployees)" :colspan="!sideBySideView || (sideBySideView && showEmployees && showPatrimonies) ? '4' : '2'" style="padding-left: 0; padding-right: 0;">
                              <v-simple-table>
                                <template v-slot:default>
                                  <tbody>
                                    <tr v-for="activity in operation.activities" :key="activity.activityId">
                                      <td v-if="!sideBySideView || (sideBySideView && showEmployees)"> {{ activity.employee }} </td>
                                      <td v-if="!sideBySideView || (sideBySideView && showEmployees && showPatrimonies)"> {{ activity.patrimony ? activity.patrimony : '-' }} </td>
                                      <td v-if="!sideBySideView || (sideBySideView && showEmployees && showPatrimonies)"> {{ activity.implement ? activity.implement : '-' }} </td>
                                      <td v-if="!sideBySideView || (sideBySideView && showEmployees)"> {{ activity.duration }} </td>
                                    </tr>
                                  </tbody>
                                </template>
                              </v-simple-table>
                            </td>
                            
                            <td :colspan="!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses) ? '2' : '1'" v-if="((!sideBySideView || (sideBySideView && showConsumedItems)) && operationGroup.operationType != 'soil_preparation')">
                              <v-simple-table>
                                <template v-slot:default>
                                  <tbody v-if="operationGroup.operationType != 'crop'">
                                    <tr v-for="dummyExitMovementItem in operation.dummyExitMovementItems" :key="dummyExitMovementItem.dummyExitMovementItemId">
                                      <td> {{ dummyExitMovementItem.item }} </td>
                                      <td style="min-width: 130px;" v-if="!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)"> {{ dummyExitMovementItem.dose }} </td>
                                    </tr>
                                  </tbody>
                                  <tbody v-else>
                                    <tr v-for="dummyEntryMovementItem in operation.dummyEntryMovementItems" :key="dummyEntryMovementItem.dummyEntryMovementItemId">
                                      <td> {{ dummyEntryMovementItem.item }} </td>
                                      <td style="min-width: 130px;" v-if="!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)"> {{ dummyEntryMovementItem.quantity }} </td>
                                    </tr>
                                  </tbody>
                                </template>
                              </v-simple-table>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>

                  </v-card-text>
                </v-card>
              </template>

              <template v-slot:default>
                <v-card
                  :color="operationGroup.layout == 'planning' ? shadeColor(operationTypeColor(operationGroup.operationType), 50) : operationTypeColor(operationGroup.operationType)"
                >
                  <v-card-title class="title">
                    {{operationGroup.layout == 'planning' ? operationGroup.planningOperationDate : operationGroup.executionOperationDate | formatDate}} - {{operationGroup.operationTypeText}} - {{operationGroup.culture}} {{operationGroup.layout == 'planning' ? "(Planejado)" : "(Realizado)"}}
                  </v-card-title>
                  <v-card-text class="white text--primary">

                    <v-simple-table>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left">Talhão(ões)</th>
                            <th class="text-left">Safra</th>
                            <th class="text-left">Área</th>
                            <th v-if="!sideBySideView || (sideBySideView && showEmployees)" class="text-left">Funcionário</th>
                            <th v-if="!sideBySideView || (sideBySideView && showEmployees && showPatrimonies)" class="text-left">Patrimônio</th>
                            <th v-if="!sideBySideView || (sideBySideView && showEmployees && showPatrimonies)" class="text-left">Implemento</th>
                            <th v-if="!sideBySideView || (sideBySideView && showEmployees)" class="text-left">Duração</th>
                            <th v-if="((!sideBySideView || (sideBySideView && showConsumedItems)) && operationGroup.operationType != 'soil_preparation' && operationGroup.operationType != 'crop')" class="text-left" >Itens Consumidos</th>
                            <th v-if="((!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)) && operationGroup.operationType != 'soil_preparation' && operationGroup.operationType != 'crop')" class="text-left" >Dose/ha</th>
                            <th v-if="((!sideBySideView || (sideBySideView && showConsumedItems)) && operationGroup.operationType == 'crop')" class="text-left" >Produtos Produzidos</th>
                            <th v-if="((!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)) && operationGroup.operationType == 'crop')" class="text-left" >Qtd.</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="operation in (operationGroup.executionOperations.length > 0 ? operationGroup.executionOperations : operationGroup.planningOperations)"
                            :key="operation.operationId + 'operationLine'"
                          >
                            <td v-if="operation.periods">
                              <div  v-for="(period, i) in operation.periods" :key="operation.operationId + period + i">
                                {{ period }}
                              </div>
                            </td>
                            <td v-else>
                              {{ operation.period }}
                            </td>
                            <td>{{ operation.harvest }}</td>
                            <td>{{ operation.totalArea | formatNumber }} ha</td>
                            <td v-if="!sideBySideView || (sideBySideView && showEmployees)" :colspan="!sideBySideView || (sideBySideView && showEmployees && showPatrimonies) ? '4' : '2'" style="padding-left: 0; padding-right: 0;">
                              <v-simple-table>
                                <template v-slot:default>
                                  <tbody>
                                    <tr v-for="activity in operation.activities" :key="activity.activityId">
                                      <td v-if="!sideBySideView || (sideBySideView && showEmployees)"> {{ activity.employee }} </td>
                                      <td v-if="!sideBySideView || (sideBySideView && showEmployees && showPatrimonies)"> {{ activity.patrimony ? activity.patrimony : '-' }} </td>
                                      <td v-if="!sideBySideView || (sideBySideView && showEmployees && showPatrimonies)"> {{ activity.implement ? activity.implement : '-' }} </td>
                                      <td v-if="!sideBySideView || (sideBySideView && showEmployees)"> {{ activity.duration }} </td>
                                    </tr>
                                  </tbody>
                                </template>
                              </v-simple-table>
                            </td>
                            
                            <td :colspan="!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses) ? '2' : '1'" v-if="((!sideBySideView || (sideBySideView && showConsumedItems)) && operationGroup.operationType != 'soil_preparation')">
                              <v-simple-table>
                                <template v-slot:default v-if="operationGroup.operationType != 'crop'">
                                  <tbody v-if="operation.exitMovementItems">
                                    <tr v-for="exitMovementItem in operation.exitMovementItems" :key="exitMovementItem.exitMovementItemId">
                                      <td> {{ exitMovementItem.item }} </td>
                                      <td style="min-width: 130px;" v-if="!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)"> {{ exitMovementItem.dose }} </td>
                                    </tr>
                                  </tbody>
                                  <tbody v-else>
                                    <tr v-for="dummyExitMovementItem in operation.dummyExitMovementItems" :key="dummyExitMovementItem.dummyExitMovementItemId">
                                      <td> {{ dummyExitMovementItem.item }} </td>
                                      <td style="min-width: 130px;" v-if="!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)"> {{ dummyExitMovementItem.dose }} </td>
                                    </tr>
                                  </tbody>
                                </template>
                                <template v-slot:default v-else>
                                  <tbody v-if="operation.entryMovementItems">
                                    <tr v-for="entryMovementItem in operation.entryMovementItems" :key="entryMovementItem.entryMovementItemId">
                                      <td> {{ entryMovementItem.item }} </td>
                                      <td style="min-width: 130px;" v-if="!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)"> {{ entryMovementItem.quantity }} </td>
                                    </tr>
                                  </tbody>
                                  <tbody v-else>
                                  <tr v-for="dummyEntryMovementItem in operation.dummyEntryMovementItems" :key="dummyEntryMovementItem.dummyEntryMovementItemId">
                                    <td> {{ dummyEntryMovementItem.item }} </td>
                                    <td style="min-width: 130px;" v-if="!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)"> {{ dummyEntryMovementItem.quantity }} </td>
                                  </tr>
                                </tbody>
                                </template>
                              </v-simple-table>
                            </td>
                            <!-- <td v-if="operationGroup.operationType != 'soil_preparation'">
                              <div v-for="consumedItem in operation.consumedItems" :key="consumedItem.consumedItemId + 'item'">
                                {{ consumedItem.item }}
                              </div>
                            </td>
                            <td v-if="operationGroup.operationType != 'soil_preparation'">
                              <div v-for="consumedItem in operation.consumedItems" :key="consumedItem.consumedItemId + 'dose'">
                                {{ consumedItem.dose }}
                              </div>
                            </td> -->
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>

                  </v-card-text>
                </v-card>
                
                <v-card
                  v-if="!sideBySideView && operationGroup.layout == 'planning_execution'"
                  :color="shadeColor(operationTypeColor(operationGroup.operationType), 50)"
                >
                  <v-card-title class="title">
                    {{operationGroup.planningOperationDate | formatDate}} - {{operationGroup.operationTypeText}} - {{operationGroup.culture}} (Planejado)
                  </v-card-title>
                  <v-card-text class="white text--primary">

                    <v-simple-table>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left">Talhão(ões)</th>
                            <th class="text-left">Safra</th>
                            <th class="text-left">Área</th>
                            <th class="text-left">Funcionário</th>
                            <th v-if="!sideBySideView" class="text-left">Patrimônio</th>
                            <th v-if="!sideBySideView" class="text-left">Implemento</th>
                            <th class="text-left">Duração</th>
                            <th class="text-left" v-if="operationGroup.operationType != 'soil_preparation' && operationGroup.operationType != 'crop'">Itens Consumidos</th>
                            <th class="text-left" v-if="!sideBySideView && operationGroup.operationType != 'soil_preparation'  && operationGroup.operationType != 'crop'">Dose/ha</th>
                            <th class="text-left" v-if="operationGroup.operationType == 'crop'">Produtos Produzidos</th>
                            <th class="text-left" v-if="!sideBySideView && operationGroup.operationType == 'crop'">Qtd.</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="operation in operationGroup.planningOperations"
                            :key="operation.operationId + 'operationLine'"
                          >
                            <td v-if="operation.periods">
                              <div  v-for="(period, i) in operation.periods" :key="operation.operationId + period + i">
                                {{ period }}
                              </div>
                            </td>
                            <td>{{ operation.harvest }}</td>
                            <td>{{ operation.totalArea | formatNumber }} ha</td>
                            <td :colspan="sideBySideView ? '2' : '4'" style="padding-left: 0; padding-right: 0;">
                              <v-simple-table>
                                <template v-slot:default>
                                  <tbody>
                                    <tr v-for="activity in operation.activities" :key="activity.activityId">
                                      <td> {{ activity.employee }} </td>
                                      <td v-if="!sideBySideView"> {{ activity.patrimony ? activity.patrimony : '-' }} </td>
                                      <td v-if="!sideBySideView"> {{ activity.implement ? activity.implement : '-' }} </td>
                                      <td> {{ activity.duration }} </td>
                                    </tr>
                                  </tbody>
                                </template>
                              </v-simple-table>
                            </td>
                            
                            <td colspan="2" v-if="operationGroup.operationType != 'soil_preparation'">
                              <v-simple-table>
                                <template v-slot:default>
                                  <tbody v-if="operationGroup.operationType != 'crop'">
                                    <tr v-for="dummyExitMovementItem in operation.dummyExitMovementItems" :key="dummyExitMovementItem.dummyExitMovementItemId">
                                      <td> {{ dummyExitMovementItem.item }} </td>
                                      <td style="min-width: 130px;" v-if="!sideBySideView"> {{ dummyExitMovementItem.dose }} </td>
                                    </tr>
                                  </tbody>
                                  <tbody v-else>
                                    <tr v-for="dummyEntryMovementItem in operation.dummyEntryMovementItems" :key="dummyEntryMovementItem.dummyEntryMovementItemId">
                                      <td> {{ dummyEntryMovementItem.item }} </td>
                                      <td style="min-width: 130px;" v-if="!sideBySideView || (sideBySideView && showConsumedItems && showConsumedItemDoses)"> {{ dummyEntryMovementItem.quantity }} </td>
                                    </tr>
                                  </tbody>
                                </template>
                              </v-simple-table>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>

                  </v-card-text>
                </v-card>
              </template>
            </v-timeline-item>
          </v-timeline>

        </template>
      </v-simple-table>
    </div>
    
  </div>
</template>

<script>
import gql from 'graphql-tag'
import moment from "moment";
import BaseIndex from "@shared_components/base_index.vue";
import DateRangeField from "@shared_components/date_range_field.vue"
import BasePrint from "@shared_components/base_print.vue";

export default {
  data() {
    return {
      tableItems: [],
      planningExecutionOperationsReport: null,
      sideBySideView: this.$vuetify.breakpoint.mdAndUp,
      showEmployees: true,
      showPatrimonies: false,
      showConsumedItems: true, //usado tambem para produto produzido
      showConsumedItemDoses: false, //usado tambem para quantidade de produto produzido
      loading: false,
      harvestListLoading: false,
      harvestList: [],
      harvestId: null,
      cultureList: [],
      allCultureList: [],
      cultureListLoading: false,
      cultureId: null,
      periodListLoading: false,
      periodList: [],
      allPeriodList: [],
      operationTypeList: [],
      periodIds: [],
      operationTypeIds: [],
      fieldsList: [],
      fieldIds: [],
      startDate: null,
      endDate: null,
      monthFilter: moment().format('YYYY-MM'),
      periodFilterOptions: [
        { "key": null, "name": "Sem filtro de data" },
        { "key": "DAY", "name": "Hoje" },
        { "key": "WEEK", "name": "Esta Semana" },
        { "key": "MONTH", "name": "Este Mês" },
        { "key": "OTHER", "name": "Customizado" },
      ],
      periodFilterModel: "MONTH",
      periodFilterRangeModel: [moment().startOf('week').format('YYYY-MM-DD'), moment().endOf('week').format('YYYY-MM-DD')]
    };
  },
  props: {
  },
  components: {
    BaseIndex: BaseIndex,
    DateRangeField: DateRangeField,
    BasePrint: BasePrint,
  },
  created() {
    this.loadAllHarvests();
  },
  mounted() { 
  },
  computed: {
    tableHeaders: function () { }
  },
  methods: {
    loadExecutionOperationReport: function() {
      this.loading = true;
      this.runDateFilter();
      this.$apollo
        .query({
          query: LOAD_EXECUTION_OPERATIONS_REPORT,
          variables: {
            harvestId: this.harvestId, 
            cultureId: this.cultureId, 
            periodIds: this.periodIds,
            fieldIds: this.fieldIds,
            operationTypeIds: this.operationTypeIds, 
            startDate: this.startDate, 
            endDate: this.endDate
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.planningExecutionOperationsReport = response.data.accountContext.planningExecutionOperationsReport;
          // console.log("planningExecutionOperationsReport", this.planningExecutionOperationsReport);
        })
        .catch(response => console.log(response))
        .finally(() => (this.loading = false));
    },
    loadAllHarvests: function() {
        this.harvestListLoading = true;
        this.$apollo
          .query({
            query: LOAD_ALL_HARVEST_QUERY,
            fetchPolicy: "no-cache"
          })
          .then(response => {
            this.harvestList = response.data.accountContext.allHarvests;
            this.operationTypeList = response.data.accountGlobalSharedContext.allOperationTypes;
            this.fieldsList = response.data.groundContext.allFields;
            this.allCultureList = response.data.accountContext.allCulturesForAccount;
            this.allPeriodList = response.data.accountContext.allPeriods;
          })
          .catch(response => console.log(response))
          .finally(() => (this.harvestListLoading = false));
    },
    loadCulturesFromHarvest: function() {
      if (this.harvestId){
        this.cultureListLoading = true;
        this.$apollo
          .query({
            query: CULTURES_FROM_HARVEST_QUERY,
            variables: {
              harvestId: this.harvestId
            },
            fetchPolicy: "no-cache"
          })
          .then(response => {
            this.cultureList = response.data.accountContext.allCulturesFromHarvest;
            this.cultureId = null;
            this.periodIds = [];
          })
          .catch(response => console.log(response))
          .finally(() => (this.cultureListLoading = false));
        
        this.loadPeriodsFromHarvestAndCulture();
      }
    },
    loadPeriodsFromHarvestAndCulture: function() {
      this.periodListLoading = true;
      this.$apollo
        .query({
          query: PERIODS_FROM_HARVEST_AND_CULTURES_QUERY,
          variables: {
            harvestId: this.harvestId,
            cultureId: this.cultureId
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.periodList = response.data.accountContext.allPeriods;
          this.periodIds = [];
        })
        .catch(response => console.log(response))
        .finally(() => (this.periodListLoading = false));
    },
    removeSelectedPeriod: function(item) {
      const index = this.periodIds.indexOf(item.id)
      if (index >= 0) this.periodIds.splice(index, 1)
    },
    operationTypeColor: function(operationType) {
      switch(operationType) {
        case "soil_preparation":
          return "#8D6E63"; //brown
        case "planting":
          return "#66BB6A"; //green
        case "forage_planting":
          return "#7CB342"; //light-green
        case "fertilization":
          return "#FFA726"; //orange
        case "application":
          return "#FF7043"; //deep-orange
        case "crop":
          return "#29B6F6"; //light-blue
        default:
          return "#78909C"; //blue-grey
      }
    },
    operationTypeIcon: function(operationType) {
      switch(operationType) {
        case "soil_preparation":
          return "mdi-tractor";
        case "planting":
          return "mdi-seed";
        case "forage_planting":
          return "mdi-barley";
        case "fertilization":
          return "mdi-sprinkler"; 
        case "application":
          return "mdi-spray";
        case "crop":
          return "mdi-sprout";
        default:
          return "mdi-leaf";
      }
    },
    runDateFilter: function() {
      if (this.periodFilterModel == null) {
        this.startDate = null;
        this.endDate   = null;
      } else if (this.periodFilterModel != "OTHER") {
        this.startDate = moment().startOf(this.periodFilterModel.toLocaleLowerCase()).format('YYYY-MM-DD');
        this.endDate   = moment().endOf(this.periodFilterModel.toLocaleLowerCase()).format('YYYY-MM-DD');
      } else {
        if (this.periodFilterRangeModel.length == 2) {
          this.startDate = this.periodFilterRangeModel[0];
          this.endDate   = this.periodFilterRangeModel[1];
        }
      }
      // console.log(this.startDate, this.endDate);
      // this.loadExecutionOperationReport();
      // this.startDate = moment(this.monthFilter, 'YYYY-MM').startOf('week').format('YYYY-MM-DD');
      // this.endDate   = moment(this.monthFilter, 'YYYY-MM').endOf('week').format('YYYY-MM-DD');
    },
    shadeColor: function(color, percent) {

      var R = parseInt(color.substring(1,3),16);
      var G = parseInt(color.substring(3,5),16);
      var B = parseInt(color.substring(5,7),16);

      R = parseInt(R * (100 + percent) / 100);
      G = parseInt(G * (100 + percent) / 100);
      B = parseInt(B * (100 + percent) / 100);

      R = (R<255)?R:255;  
      G = (G<255)?G:255;  
      B = (B<255)?B:255;  

      var RR = ((R.toString(16).length==1)?"0"+R.toString(16):R.toString(16));
      var GG = ((G.toString(16).length==1)?"0"+G.toString(16):G.toString(16));
      var BB = ((B.toString(16).length==1)?"0"+B.toString(16):B.toString(16));

      return "#"+RR+GG+BB;
    }
  }
};



const LOAD_EXECUTION_OPERATIONS_REPORT = gql `
query ($harvestId: ID, $cultureId: ID, $periodIds: [ID!], $operationTypeIds: [ID!], $fieldIds: [ID!], $startDate: ISO8601Date, $endDate: ISO8601Date) {
  accountContext {
    planningExecutionOperationsReport(harvestId: $harvestId, cultureId: $cultureId, periodIds: $periodIds, operationTypeIds: $operationTypeIds, fieldIds: $fieldIds, startDate: $startDate, endDate: $endDate) 
  }
}
`;

const LOAD_ALL_HARVEST_QUERY = gql `
  {
    accountContext {
      allHarvests {
        id
        name
      }
      allCulturesForAccount {
        id
        name
      }
      allPeriods {
        id
        name
        additionalInfo
        secondAdditionalInfo
      }
    }
    accountGlobalSharedContext {
      allOperationTypes {
        id
        name
      }
    }
    groundContext {
      allFields {
        id
        name
      }
    }
  }
`;

const CULTURES_FROM_HARVEST_QUERY = gql `
  query ($harvestId: ID!) {
    accountContext {
      allCulturesFromHarvest(harvestId: $harvestId){
        id
        name
      }
    }
  }
`;

const PERIODS_FROM_HARVEST_AND_CULTURES_QUERY = gql `
  query ($harvestId: ID, $cultureId: ID) {
    accountContext {
      allPeriods(harvestId: $harvestId, cultureId: $cultureId){
        id
        name
        additionalInfo
        secondAdditionalInfo
      }
    }
  }
`;

</script>
<style scoped>
</style>
